import React, { useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { FileText, ArrowRight, Lightning, Copy, Clock, Flask, Warning as WarningIcon } from '@phosphor-icons/react';

const PaperToProtocolPage = () => {
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const convert = async () => {
    if (!text.trim()) { toast.error('Paste the methods section text'); return; }
    setLoading(true);
    try {
      const res = await api.paperToProtocol(text, title || 'Untitled');
      setResult(res.protocol);
      toast.success('Protocol extracted');
    } catch (e) { toast.error('Conversion failed'); }
    setLoading(false);
  };

  const saveAsProtocol = async () => {
    if (!result) return;
    try {
      await api.createProtocol({
        title: result.title || 'Extracted Protocol',
        description: result.description || '',
        steps: (result.steps || []).map(s => ({
          instruction: s.instruction,
          duration_minutes: s.duration_minutes,
          notes: s.notes || '',
        })),
        tags: ['extracted', 'paper-to-protocol'],
        materials: result.materials || [],
      });
      toast.success('Protocol saved!');
    } catch (e) { toast.error('Save failed'); }
  };

  const copyProtocol = () => {
    if (!result) return;
    const text = result.steps?.map((s, i) => `${i + 1}. ${s.instruction}${s.duration_minutes ? ` (${s.duration_minutes} min)` : ''}`).join('\n') || '';
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div data-testid="paper-protocol-page">
      <div className="mb-6">
        <h1 className="heading-display flex items-center gap-2"><FileText size={24} className="text-indigo-500" /> Paper to Protocol</h1>
        <p className="text-slate-400 mt-1">Paste a methods section from a paper and AI will extract a structured, executable protocol</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="card-flat p-5" data-testid="paper-input">
          <h3 className="heading-card mb-3">Methods Section</h3>
          <input className="input-field w-full mb-3" placeholder="Paper title (optional)" value={title} onChange={e => setTitle(e.target.value)} data-testid="paper-title-input" />
          <textarea
            className="input-field w-full font-mono text-xs"
            rows={16}
            placeholder="Paste the methods section text here...&#10;&#10;Example: Cells were cultured in DMEM supplemented with 10% FBS at 37°C..."
            value={text}
            onChange={e => setText(e.target.value)}
            data-testid="methods-textarea"
          />
          <p className="text-[10px] text-slate-400 mt-1">{text.length} characters</p>
          <Button className="btn-primary w-full mt-3 flex items-center justify-center gap-2" onClick={convert} disabled={loading} data-testid="convert-btn">
            {loading ? <div className="spinner-sm" /> : <><Lightning size={16} /> Extract Protocol</>}
          </Button>
        </div>

        {/* Output */}
        <div className="card-flat p-5" data-testid="protocol-output">
          <div className="flex items-center justify-between mb-3">
            <h3 className="heading-card">Extracted Protocol</h3>
            {result && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="btn-secondary text-xs" onClick={copyProtocol} data-testid="copy-protocol-btn">
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
                <Button size="sm" className="btn-primary text-xs" onClick={saveAsProtocol} data-testid="save-protocol-btn">Save as Protocol</Button>
              </div>
            )}
          </div>

          {!result ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <FileText size={40} className="mb-3 opacity-40" />
              <p className="text-sm">Paste methods text and click Extract</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <h4 className="font-semibold text-sm" style={{ color: '#F1F5F9' }}>{result.title}</h4>
                {result.description && <p className="text-xs text-slate-400 mt-1">{result.description}</p>}
                {result.estimated_total_time && (
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Clock size={12} /> Est. time: {result.estimated_total_time}</p>
                )}
              </div>

              {/* Materials & Equipment */}
              <div className="grid grid-cols-2 gap-3">
                {result.materials?.length > 0 && (
                  <div className="p-3 bg-blue-900/20 rounded-lg">
                    <p className="text-xs font-semibold text-indigo-400 mb-1 flex items-center gap-1"><Flask size={12} /> Materials</p>
                    <ul className="text-xs text-blue-800 space-y-0.5">
                      {result.materials.map((m, i) => <li key={i}>- {m}</li>)}
                    </ul>
                  </div>
                )}
                {result.equipment?.length > 0 && (
                  <div className="p-3 bg-violet-50 rounded-lg">
                    <p className="text-xs font-semibold text-violet-700 mb-1">Equipment</p>
                    <ul className="text-xs text-violet-800 space-y-0.5">
                      {result.equipment.map((e, i) => <li key={i}>- {e}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {result.safety_notes && (
                <div className="p-3 bg-amber-900/20 rounded-lg">
                  <p className="text-xs font-semibold text-amber-300 flex items-center gap-1"><WarningIcon size={12} /> Safety Notes</p>
                  <p className="text-xs text-amber-800 mt-1">{result.safety_notes}</p>
                </div>
              )}

              {/* Steps */}
              <div>
                <p className="text-xs font-semibold text-slate-400 mb-2">Steps ({result.steps?.length || 0})</p>
                <div className="space-y-2">
                  {result.steps?.map((step, i) => (
                    <div key={i} className="flex gap-3 p-3 rounded-lg border border-slate-700 hover:border-slate-700 transition-colors" data-testid={`step-${i}`}>
                      <div className="w-7 h-7 rounded-full bg-indigo-900/300 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {step.step_number || i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-200">{step.instruction}</p>
                        <div className="flex items-center gap-3 mt-1">
                          {step.duration_minutes && <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} /> {step.duration_minutes} min</span>}
                          {step.temperature && <span className="text-[10px] text-slate-400">{step.temperature}</span>}
                          {step.notes && <span className="text-[10px] text-blue-500">{step.notes}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaperToProtocolPage;
